<template>
  <EditorModal
    :title="modalTitle"
    :subtitle="modalSubtitle"
    show-close-button
    @on-close-modal="handleClose"
  >
    <template #content>
      <div class="workflow-viewer__content">
        <div
          v-if="loading"
          class="workflow-viewer__state"
        >
          <i class="far fa-spinner-third fa-spin" />
          <span>{{ t('global.loading') }}</span>
        </div>
        <div
          v-else-if="error"
          class="workflow-viewer__state workflow-viewer__state--error"
        >
          <i class="far fa-triangle-exclamation" />
          <span>{{ error }}</span>
        </div>
        <FlowRenderer
          v-else-if="flow"
          ref="flowRendererRef"
          :flow="flow"
          :type="flowType"
          @add-operator="handleAddOperator"
          @update-step="handleUpdateStep"
          @delete-step="handleDeleteStep"
          @decrease-branches="handleDecreaseBranches"
          @replace-step="handleReplaceStep"
          @menu-action="handleMenuAction"
          @start-flow="handleStartFlow"
          @merge-branch="handleMergeBranch"
        />
        <div
          v-else
          class="workflow-viewer__state"
        >
          <i class="far fa-diagram-project" />
          <span>Aucun élément à afficher pour ce workflow.</span>
        </div>
      </div>
      <!-- Send Test Modal (outside the v-if/v-else chain) -->
      <SendTestModal
        v-if="showSendTestModal"
        :type-of-input="sendTestTemplateType"
        @on-validate="sendTestTemplate"
        @on-close-dialog="closeSendTestModal"
      />
    </template>
  </EditorModal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onMounted, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import EditorModal from '@/components/modals/EditorModal.vue';
import FlowRenderer from '@/components/flows/FlowRenderer.vue';
import SendTestModal from '@/components/modals/SendTestModal.vue';
import { fetchFlowStructure } from '@/composables/flows/useFlowApi';
import { useFlowEditor, AddOperatorPayload } from '@/composables/flows/useFlowEditor';
import { FlowStructureType } from '@/types/flow-structure';
import { useStore } from '@/store';
import { TypeCampaignEnum } from '@/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { showToastSuccess, showToastError } from '@/helpers';

export default defineComponent({
  name: 'WorkflowViewer',
  components: {
    EditorModal,
    FlowRenderer,
    SendTestModal,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const store = useStore();

    // Flow identifier based on route params
    const flowIdentifier = computed(() => {
      const typeParam = String(route.params.type || 'automation');
      const idParam = Number(route.params.id);
      const type = typeParam === 'automation'
        ? TypeCampaignEnum.AUTOMATION
        : TypeCampaignEnum.BULK;
      return { id: idParam, type };
    });

    // Use flowEditor with the flowIdentifier to bind to the store
    const flowEditor = computed(() => useFlowEditor(flowIdentifier.value));
    const flow = computed(() => flowEditor.value.flow.value);
    const loading = ref(false);
    const error = ref('');

    const flowType = ref<FlowStructureType>(TypeCampaignEnum.AUTOMATION);
    const campaignId = ref<number>(0);

    const modalTitle = ref('Visualisation du workflow');
    const modalSubtitle = ref('');

    const parseRoute = () => {
      const typeParam = String(route.params.type || 'automation');
      if (typeParam !== 'automation' && typeParam !== 'bulk') {
        error.value = 'Type de workflow inconnu.';
        return false;
      }
      flowType.value = typeParam === 'automation' ? TypeCampaignEnum.AUTOMATION : TypeCampaignEnum.BULK;
      const idParam = Number(route.params.id);
      if (!Number.isFinite(idParam)) {
        error.value = 'Identifiant de campagne invalide.';
        return false;
      }
      campaignId.value = idParam;
      modalTitle.value = typeParam === 'automation'
        ? 'Workflow automation'
        : 'Workflow campagne ponctuelle';
      modalSubtitle.value = `Campagne n°${idParam}`;
      return true;
    };

    const loadWorkflow = async () => {
      if (!parseRoute()) {
        return;
      }
      if (campaignId.value === null) {
        return;
      }
      loading.value = true;
      error.value = '';
      try {
        const rawFlow = await fetchFlowStructure(flowType.value, campaignId.value);
        // Open flow in the store
        store.dispatch('flowEditor/openFlow', {
          id: campaignId.value,
          flowData: rawFlow,
          type: flowIdentifier.value.type,
          name: `Campagne n°${campaignId.value}`,
        });
      } catch (err: any) {
        error.value = err?.message ?? 'Une erreur est survenue pendant le chargement du workflow.';
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      if (window.history.length > 1) {
        router.back();
        return;
      }
      router.push({ name: 'workflow' });
    };

    const handleAddOperator = (payload: AddOperatorPayload) => {
      flowEditor.value.addOperator(payload);
    };

    const handleUpdateStep = (payload: { stepId: string; data: any }) => {
      flowEditor.value.updateOperator(payload.stepId, payload.data);
    };

    const handleDeleteStep = (payload: { stepId: string }) => {
      flowEditor.value.removeOperator(payload.stepId);
    };

    const handleDecreaseBranches = (payload: { stepId: string; outputsToRemove: string[] }) => {
      flowEditor.value.removeBranches(payload.stepId, payload.outputsToRemove);
    };

    const handleReplaceStep = (payload: { operator: any; data: any; stepId: string }) => {
      flowEditor.value.replaceOperator(payload.stepId, payload.operator, payload.data);
    };

    const handleMergeBranch = (payload: { sourceStepId: string; sourceOutput: number; targetStepId: string }) => {
      flowEditor.value.addConnection(payload.sourceStepId, payload.sourceOutput, payload.targetStepId);
    };

    // Handle start flow from empty state
    const flowRendererRef = ref<any>(null);
    const handleStartFlow = (type: 'bulk' | 'automation') => {
      if (type === 'bulk') {
        // For bulk campaigns, directly open the AudienceSetup form
        // We need to trigger the operator panel with the AudienceSetup operator pre-selected
        if (flowRendererRef.value) {
          flowRendererRef.value.openTriggerForm('boxaudiencesetup');
        }
      } else {
        // For automation, open the trigger selection panel
        if (flowRendererRef.value) {
          flowRendererRef.value.openTriggerPanel();
        }
      }
    };

    // Send test message handling
    const showSendTestModal = ref(false);
    const sendTestTemplateType = ref('');
    const sendTestOperatorId = ref('');
    const sendTestStep = ref<any>(null);

    const handleMenuAction = (actionId: string, step: any) => {
      // Map action IDs to template types
      const actionToTemplateType: Record<string, string> = {
        'send-test-email': 'email',
        'send-test-sms': 'sms',
        'send-test-whatsapp': 'whatsapp',
        'send-test-push': 'pushnotification',
      };

      const templateType = actionToTemplateType[actionId];
      if (templateType) {
        sendTestTemplateType.value = templateType;
        sendTestOperatorId.value = step.id;
        sendTestStep.value = step;
        showSendTestModal.value = true;
        store.commit('general/showSendTestModal'); // Required by SendTestModal component
      }
    };

    const sendTestTemplate = async (params: any[]) => {
      if (!sendTestTemplateType.value || !sendTestStep.value) {
        showSendTestModal.value = false;
        return;
      }

      store.commit('general/showTheSpinner');

      try {
        const operatorData = sendTestStep.value.parameters || {};
        const idShop = UserState.activeShop?.id ?? 0;

        const getData: Record<string, any> = {
          type: sendTestTemplateType.value,
          params,
        };

        // Add sender information for email/sms/whatsapp
        if (['email', 'sms', 'whatsapp', 'richtexting'].includes(sendTestTemplateType.value)) {
          getData.informations = {
            fromName: operatorData.template_message_expname,
          };

          if (sendTestTemplateType.value === 'email') {
            getData.informations.fromEmail = operatorData.template_message_expmail;
            getData.informations.subject = operatorData.template_message_subject;
            getData.informations.previewText = operatorData.template_message_preview_text;
          }

          // Ensure all informations are proper objects (not stringified JSON)
          Object.keys(getData.informations).forEach((key) => {
            const val = getData.informations[key];
            if (typeof val === 'string') {
              try {
                getData.informations[key] = JSON.parse(val);
              } catch (e) {
                // Keep as string if parsing fails
              }
            }
          });
        }

        const templateId = operatorData.template_display_selector_id;
        if (!templateId) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          store.commit('general/hideTheSpinner');
          showSendTestModal.value = false;
          return;
        }

        const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
        const result = await nestCall(
          'get',
          NestApiType.DEFAULT,
          `/template/test/${idShop}/${templateId}/${dataQuery}`,
          {},
          '',
          {},
          false,
        );

        if (result && result.success) {
          if (sendTestTemplateType.value === 'email') {
            await showToastSuccess('emailSent');
          } else {
            await showToastSuccess('smsSent');
          }
        } else {
          await showToastError(result && result.message ? t(`errorMessages.${result.message}`) : t('errorMessages.GENERIC_ERROR'));
        }
      } catch (err: any) {
        await showToastError(err?.message ?? t('errorMessages.GENERIC_ERROR'));
      }

      store.commit('general/hideTheSpinner');
      sendTestTemplateType.value = '';
      sendTestOperatorId.value = '';
      sendTestStep.value = null;
      showSendTestModal.value = false;
    };

    const closeSendTestModal = () => {
      sendTestTemplateType.value = '';
      sendTestOperatorId.value = '';
      sendTestStep.value = null;
      showSendTestModal.value = false;
    };

    watch(
      () => [route.params.type, route.params.id],
      () => {
        loadWorkflow();
      },
    );

    onMounted(() => {
      loadWorkflow();
    });

    return {
      t,
      flow,
      loading,
      error,
      flowType,
      modalTitle,
      modalSubtitle,
      handleClose,
      handleAddOperator,
      handleUpdateStep,
      handleDeleteStep,
      handleDecreaseBranches,
      handleReplaceStep,
      handleMenuAction,
      handleStartFlow,
      handleMergeBranch,
      flowRendererRef,
      showSendTestModal,
      sendTestTemplateType,
      sendTestOperatorId,
      sendTestStep,
      sendTestTemplate,
      closeSendTestModal,
    };
  },
});
</script>

<style scoped>
.workflow-viewer__content {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 6rem);
  background: #eef2f7;
}

.workflow-viewer__state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #4f5d75;
  font-size: 1rem;
}

.workflow-viewer__state i {
  font-size: 1.4rem;
}

.workflow-viewer__state--error {
  color: #d64550;
}
</style>
